import React, { useEffect, useState, useRef } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { dvCreate, dvList } from '../../../api/dataverseClient';
import {
  ImportSourceType, ImportStatus, SOURCE_LABELS,
  fetchPlannerTasks, fetchPlannerBuckets,
  createImportJob, updateImportJob,
  batchInsert, BatchProgress, IMPORT_COLLECTION_COLORS,
} from '../../../services/importService';
import { ImportTaskMeta } from '../../../services/importConnectors';
import { ImportSummary, emptySummary, serializeChecklistState } from '../../../services/importChecklistService';
import { ResourceMap } from './ResourceMappingStep';

// ── Post-import summary helpers (feed the verification checklist) ──────
function countAssignment(bodies: Record<string, unknown>[]): { unassigned: number; unmatched: number } {
  let unassigned = 0;
  const unmatched = new Set<string>();
  for (const b of bodies) {
    const hasId = !!b.dagrun_pm_assigneeid;
    const name = typeof b.dagrun_pm_assigneename === 'string' ? b.dagrun_pm_assigneename.trim() : '';
    if (!hasId && !name) unassigned++;
    else if (!hasId && name) unmatched.add(name);
  }
  return { unassigned, unmatched: unmatched.size };
}
function countUnmappedColumns(headers: string[], mapping: Record<string, string>): number {
  return headers.filter(h => { const m = mapping[h]; return !m || m === '(skip)'; }).length;
}
async function isProjectPoolLinked(pca: IPublicClientApplication, projectId: string): Promise<boolean> {
  try {
    const rows = await dvList<{ dagrun_pm_useglobalresourcepool?: boolean }>(pca, 'dagrun_pm_projects', {
      select: ['dagrun_pm_useglobalresourcepool'], filter: `dagrun_pm_projectid eq ${projectId}`, top: 1,
    });
    return !!rows[0]?.dagrun_pm_useglobalresourcepool;
  } catch { return false; }
}

interface ImportHierarchy {
  collections: string[];
  taskMeta: ImportTaskMeta[];
  dependencyCount: number;
}

interface Props {
  pca: IPublicClientApplication;
  source: number;
  connectionData: {
    headers?: string[];
    rows?: unknown[][];
    planId?: string;
    fileName?: string;
    sourceLabel?: string;
    hierarchy?: ImportHierarchy;
  } | null;
  mapping: Record<string, string>;
  resourceMap?: ResourceMap;
  targetProjectId: string;
}

export default function RunStep({ pca, source, connectionData, mapping, resourceMap, targetProjectId }: Props) {
  const nav = useNavigate();
  const [total, setTotal]         = useState(0);
  const [imported, setImported]   = useState(0);
  const [failed, setFailed]       = useState(0);
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [status, setStatus]       = useState<'running' | 'done' | 'error'>('running');
  const [msg, setMsg]             = useState('');
  const [, setJobId]              = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, []); // eslint-disable-line

  const onBatchProgress = (p: BatchProgress) => {
    setImported(p.imported);
    setFailed(p.failed);
    setBatchIndex(p.batchIndex);
    setBatchCount(p.batchCount);
  };

  async function run() {
    try {
      const jid = await createImportJob(pca, {
        dagrun_pm_name: `Import from ${SOURCE_LABELS[source]}`,
        dagrun_pm_sourcetype: source,
        dagrun_pm_sourceidentifier: connectionData?.planId ?? connectionData?.fileName ?? connectionData?.sourceLabel ?? '',
        dagrun_pm_status: ImportStatus.InProgress,
        'dagrun_pm_targetprojectid@odata.bind': `/dagrun_pm_projects(${targetProjectId})`,
      });
      setJobId(jid);

      if (source === ImportSourceType.Planner) {
        await importPlanner(pca, jid, connectionData?.planId ?? '', targetProjectId);
      } else if (connectionData?.hierarchy && connectionData.hierarchy.collections.length > 0) {
        // Microsoft Project XML and Project Online — WBS collections + deps.
        await importHierarchy(pca, jid, connectionData, mapping, targetProjectId, connectionData.hierarchy);
      } else {
        await importRowset(pca, jid, connectionData, mapping, targetProjectId);
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Import failed');
      setStatus('error');
    }
  }

  async function importPlanner(pca: IPublicClientApplication, jid: string, planId: string, projectId: string) {
    const [tasks, buckets] = await Promise.all([
      fetchPlannerTasks(pca, planId),
      fetchPlannerBuckets(pca, planId),
    ]);
    setTotal(tasks.length);

    // Buckets become collections — created first so tasks can bind to them.
    const bucketMap: Record<string, string> = {};
    for (const b of buckets) {
      const colId = await dvCreate(pca, 'dagrun_pm_collections', {
        dagrun_pm_name: b.name,
        dagrun_pm_sortorder: 0,
        'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
      });
      bucketMap[b.id] = colId;
    }

    const bodies: Record<string, unknown>[] = tasks.map((t) => {
      const dvStatus = t.percentComplete === 100 ? 100000003 : t.percentComplete > 0 ? 100000001 : 100000000;
      const assignees = t.assignments ? Object.keys(t.assignments) : [];
      const data: Record<string, unknown> = {
        dagrun_pm_name: t.title,
        dagrun_pm_status: dvStatus,
        dagrun_pm_percentcomplete: t.percentComplete ?? 0,
        dagrun_pm_duedate: t.dueDateTime ? t.dueDateTime.split('T')[0] : null,
        dagrun_pm_assigneeid: assignees[0] ?? null,
        'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
      };
      if (t.bucketId && bucketMap[t.bucketId]) {
        data['dagrun_pm_wipcollectionid@odata.bind'] = `/dagrun_pm_collections(${bucketMap[t.bucketId]})`;
      }
      return data;
    });

    const { imported: ok, failed: fail } = await batchInsert(pca, 'dagrun_pm_tasks', bodies, {
      jobId: jid,
      onProgress: onBatchProgress,
    });

    const asg = countAssignment(bodies);
    const summary: ImportSummary = {
      ...emptySummary(),
      rowsTotal: tasks.length, rowsImported: ok, rowsFailed: fail,
      unassignedTasks: asg.unassigned, unmatchedResources: asg.unmatched,
      collectionsCreated: buckets.length,
      resourcePoolLinked: await isProjectPoolLinked(pca, projectId),
      sourceLabel: SOURCE_LABELS[ImportSourceType.Planner] ?? 'Planner',
    };
    await updateImportJob(pca, jid, {
      dagrun_pm_status: fail === 0 ? ImportStatus.Completed : ImportStatus.PartialSuccess,
      dagrun_pm_rowstotal: tasks.length,
      dagrun_pm_rowsimported: ok,
      dagrun_pm_rowsfailed: fail,
      dagrun_pm_importsummaryjson: serializeChecklistState(summary),
    });
    setStatus('done');
  }

  // Generic row-set importer shared by Excel/CSV, Project XML, Monday.com,
  // Smartsheet, Jira, Asana, ClickUp, and Trello. The mapped rows are turned
  // into task bodies and written via $batch (100 rows per Dataverse API call).
  async function importRowset(
    pca: IPublicClientApplication,
    jid: string,
    connData: typeof connectionData,
    mapping: Record<string, string>,
    projectId: string,
  ) {
    const headers: string[] = connData?.headers ?? [];
    const rows: unknown[][] = connData?.rows ?? [];
    setTotal(rows.length);

    const colToField = buildColToField(headers, mapping);
    const bodies: Record<string, unknown>[] = rows.map((row, r) =>
      applyResourceMap(buildTaskBody(row, colToField, projectId, r), resourceMap));

    const { imported: ok, failed: fail } = await batchInsert(pca, 'dagrun_pm_tasks', bodies, {
      jobId: jid,
      onProgress: onBatchProgress,
    });

    const asg = countAssignment(bodies);
    const summary: ImportSummary = {
      ...emptySummary(),
      rowsTotal: rows.length, rowsImported: ok, rowsFailed: fail,
      unassignedTasks: asg.unassigned, unmatchedResources: asg.unmatched,
      unmappedColumns: countUnmappedColumns(headers, mapping),
      resourcePoolLinked: await isProjectPoolLinked(pca, projectId),
      sourceLabel: connData?.sourceLabel ?? SOURCE_LABELS[source] ?? 'Import',
    };
    await updateImportJob(pca, jid, {
      dagrun_pm_status: fail === 0 ? ImportStatus.Completed : ImportStatus.PartialSuccess,
      dagrun_pm_rowstotal: rows.length,
      dagrun_pm_rowsimported: ok,
      dagrun_pm_rowsfailed: fail,
      dagrun_pm_importsummaryjson: serializeChecklistState(summary),
    });
    setStatus('done');
  }

  // WBS hierarchy importer (Microsoft Project XML, Project Online). Creates
  // collections from OutlineLevel 1 tasks, writes the work-item tasks bound to
  // their collection via $batch, then creates predecessor dependencies by
  // resolving each task's source UID to its new Dataverse GUID.
  async function importHierarchy(
    pca: IPublicClientApplication,
    jid: string,
    connData: typeof connectionData,
    mapping: Record<string, string>,
    projectId: string,
    hierarchy: ImportHierarchy,
  ) {
    const headers: string[] = connData?.headers ?? [];
    const rows: unknown[][] = connData?.rows ?? [];
    setTotal(rows.length);

    // 1. Collections first (need GUIDs to bind tasks). Round-robin colors.
    const collectionGuids: string[] = [];
    for (let i = 0; i < hierarchy.collections.length; i++) {
      const colId = await dvCreate(pca, 'dagrun_pm_collections', {
        dagrun_pm_name: hierarchy.collections[i] || `Phase ${i + 1}`,
        dagrun_pm_sortorder: i,
        dagrun_pm_color: IMPORT_COLLECTION_COLORS[i % IMPORT_COLLECTION_COLORS.length],
        'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
      });
      collectionGuids.push(colId);
    }

    // 2. Tasks (bound to their collection) via $batch.
    const colToField = buildColToField(headers, mapping);
    const taskBodies: Record<string, unknown>[] = rows.map((row, r) => {
      const data = applyResourceMap(buildTaskBody(row, colToField, projectId, r), resourceMap);
      const meta = hierarchy.taskMeta[r];
      const ci = meta?.collectionIndex ?? -1;
      if (ci >= 0 && collectionGuids[ci]) {
        data['dagrun_pm_wipcollectionid@odata.bind'] = `/dagrun_pm_collections(${collectionGuids[ci]})`;
      }
      // Source-specific extra fields (e.g. Project Online baselines).
      if (meta?.extraFields) Object.assign(data, meta.extraFields);
      return data;
    });

    const { ids: taskIds, imported: ok, failed: fail } = await batchInsert(pca, 'dagrun_pm_tasks', taskBodies, {
      jobId: jid,
      onProgress: onBatchProgress,
    });

    // 3. Predecessor links → dependencies. Resolve source UID → task GUID.
    const uidToGuid = new Map<string, string>();
    hierarchy.taskMeta.forEach((m, i) => { if (m.uid && taskIds[i]) uidToGuid.set(m.uid, taskIds[i] as string); });

    const depBodies: Record<string, unknown>[] = [];
    let skippedDeps = 0;
    hierarchy.taskMeta.forEach((m, i) => {
      const succ = taskIds[i];
      if (!succ) return;
      for (const pred of m.predecessors) {
        const predGuid = uidToGuid.get(pred.uid);
        if (!predGuid) { skippedDeps++; continue; }
        depBodies.push({
          dagrun_pm_dependencytype: pred.type,
          'dagrun_pm_predecessortaskid@odata.bind': `/dagrun_pm_tasks(${predGuid})`,
          'dagrun_pm_successortaskid@odata.bind': `/dagrun_pm_tasks(${succ})`,
          'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
        });
      }
    });

    if (depBodies.length > 0) {
      await batchInsert(pca, 'dagrun_pm_dependencies', depBodies, { jobId: jid });
    }
    if (skippedDeps > 0) {
      try {
        await dvCreate(pca, 'dagrun_pm_importlogs', {
          dagrun_pm_name: 'Skipped dependencies',
          dagrun_pm_rowindex: 0,
          dagrun_pm_errormessage: `${skippedDeps} predecessor link${skippedDeps === 1 ? '' : 's'} skipped — referenced task was not imported (e.g. a summary task or filtered row).`,
          dagrun_pm_issuetype: 100000000,
          dagrun_pm_resolutionstatus: 100000001,
          'dagrun_pm_importjobid@odata.bind': `/dagrun_pm_importjobs(${jid})`,
        });
      } catch { /* best effort */ }
    }

    const asg = countAssignment(taskBodies);
    const constraintsConverted = hierarchy.taskMeta.filter(
      m => m.extraFields && m.extraFields['dagrun_pm_constrainttype'] != null,
    ).length;
    // MS Project / Project Online carry a real schedule → work-calendar relevant.
    const calendarRelevant = source === ImportSourceType.ProjectProfessional || source === ImportSourceType.ProjectOnline;
    const summary: ImportSummary = {
      ...emptySummary(),
      rowsTotal: rows.length, rowsImported: ok, rowsFailed: fail,
      unassignedTasks: asg.unassigned, unmatchedResources: asg.unmatched,
      collectionsCreated: collectionGuids.length,
      dependenciesCreated: depBodies.length,
      skippedDependencies: skippedDeps,
      constraintsConverted,
      unmappedColumns: countUnmappedColumns(headers, mapping),
      resourcePoolLinked: await isProjectPoolLinked(pca, projectId),
      calendarRelevant,
      sourceLabel: connData?.sourceLabel ?? SOURCE_LABELS[source] ?? 'Import',
    };
    await updateImportJob(pca, jid, {
      dagrun_pm_status: fail === 0 ? ImportStatus.Completed : ImportStatus.PartialSuccess,
      dagrun_pm_rowstotal: rows.length,
      dagrun_pm_rowsimported: ok,
      dagrun_pm_rowsfailed: fail,
      dagrun_pm_importsummaryjson: serializeChecklistState(summary),
    });
    setStatus('done');
  }

  const pct = total > 0 ? Math.round(((imported + failed) / total) * 100) : 0;

  return (
    <div>
      <h3>Import in Progress</h3>
      <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', height: 10, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: status === 'error' ? 'var(--danger)' : 'var(--brand)',
          transition: 'width 0.3s',
        }} />
      </div>
      {status === 'running' && batchCount > 0 && (
        <div style={{ fontSize: 13, marginBottom: 6, color: 'var(--ink-2)' }}>
          Importing batch {batchIndex} of {batchCount} ({imported} task{imported === 1 ? '' : 's'} imported so far…)
        </div>
      )}
      <div style={{ fontSize: 13, marginBottom: 8, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>
        {imported + failed} / {total || '...'} tasks processed
      </div>
      <div style={{ fontSize: 13 }}>
        <span style={{ color: 'var(--success)' }}>{imported} imported</span>
        {failed > 0 && <span style={{ marginLeft: 12, color: 'var(--danger)' }}>{failed} errors</span>}
      </div>
      {msg && <div style={{ marginTop: 12, color: 'var(--ink-4)', fontSize: 12 }}>{msg}</div>}
      {status === 'done' && (
        <div style={{ marginTop: 20 }}>
          <button className="btn-primary" onClick={() => nav(`/project/${targetProjectId}`)}>View in Project</button>
        </div>
      )}
    </div>
  );
}

// Resource Mapping step result → task body. When the source assignee text
// was mapped to a real Entra user, the task gets a real dagrun_pm_assigneeid
// and the user's canonical display name. Unmapped text imports unchanged
// (text-only assignment, exactly the pre-mapping behavior).
function applyResourceMap(data: Record<string, unknown>, resourceMap?: ResourceMap): Record<string, unknown> {
  if (!resourceMap) return data;
  const nm = data.dagrun_pm_assigneename;
  if (typeof nm !== 'string') return data;
  const person = resourceMap[nm.trim()];
  if (!person) return data;
  data.dagrun_pm_assigneeid = person.aadObjectId;
  data.dagrun_pm_assigneename = person.displayName;
  return data;
}

// Map header index → target dagrun_pm field, skipping unmapped/(skip) columns.
function buildColToField(headers: string[], mapping: Record<string, string>): Record<number, string> {
  const colToField: Record<number, string> = {};
  headers.forEach((h, i) => {
    const mapped = mapping[h];
    if (mapped && mapped !== '(skip)') colToField[i] = mapped;
  });
  return colToField;
}

// Build a dagrun_pm_tasks body from one source row + the column→field map.
function buildTaskBody(row: unknown[], colToField: Record<number, string>, projectId: string, rowIndex: number): Record<string, unknown> {
  const data: Record<string, unknown> = {
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
    dagrun_pm_status: 100000000,
  };
  let hasName = false;
  for (const [idx, field] of Object.entries(colToField)) {
    const raw = row[Number(idx)];
    if (raw == null || raw === '') continue;
    const val = coerceValue(field, raw);
    if (val == null) continue;
    data[field] = val;
    if (field === 'dagrun_pm_name') hasName = true;
  }
  if (!hasName) data.dagrun_pm_name = `Imported row ${rowIndex + 1}`;
  return data;
}

// Coerce a raw cell value into the shape Dataverse expects for the given
// target field. Date fields: ISO YYYY-MM-DD. Number fields: Number(). Status
// and priority: try to map a string label; otherwise skip (return null).
function coerceValue(field: string, raw: unknown): unknown {
  if (field === 'dagrun_pm_duedate' || field === 'dagrun_pm_startdate') {
    if (raw instanceof Date) return raw.toISOString().slice(0, 10);
    const s = String(raw).trim();
    // Accept YYYY-MM-DD, or any parseable date string.
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return null;
  }
  if (field === 'dagrun_pm_percentcomplete' || field === 'dagrun_pm_estimatedhours') {
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[^\d.-]/g, ''));
    if (!isFinite(n)) return null;
    if (field === 'dagrun_pm_percentcomplete') return Math.max(0, Math.min(100, Math.round(n)));
    return n;
  }
  if (field === 'dagrun_pm_status') {
    const label = String(raw).toLowerCase().trim();
    if (/done|complete|closed|resolved/.test(label)) return 100000003;
    if (/review|qa|testing/.test(label))             return 100000002;
    if (/progress|doing|active|started/.test(label)) return 100000001;
    if (/hold|block|paused/.test(label))             return 100000004;
    if (/cancel/.test(label))                        return 100000005;
    if (/new|todo|to do|not\s*started|backlog|planned/.test(label)) return 100000000;
    return null; // unknown — skip rather than guess
  }
  if (field === 'dagrun_pm_priority') {
    const label = String(raw).toLowerCase().trim();
    if (/critical|urgent|blocker/.test(label)) return 100000000;
    if (/^high|p1/.test(label))                return 100000001;
    if (/^med/.test(label))                    return 100000002;
    if (/^low|p3|p4/.test(label))              return 100000003;
    return null;
  }
  // Strings (name, assigneename, description): coerce to string.
  return String(raw);
}
